
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface Book {
  id: string;
  title: string;
  author: string;
  description: string | null;
  cover_image_url: string | null;
  category: string;
  tags: string[] | null;
  reading_time: number | null;
  templatelink: string | null;
  order: number | null;
}

interface BookFormProps {
  book: Book | null;
  onClose: (refreshData?: boolean) => void;
}

export function BookForm({ book, onClose }: BookFormProps) {
  const isEditing = !!book;
  const [formState, setFormState] = useState<Book>({
    id: book?.id || "",
    title: book?.title || "",
    author: book?.author || "",
    description: book?.description || "",
    cover_image_url: book?.cover_image_url || "",
    category: book?.category || "",
    tags: book?.tags || [],
    reading_time: book?.reading_time || null,
    templatelink: book?.templatelink || "",
    order: book?.order || null,
  });
  const [tagsInput, setTagsInput] = useState(
    book?.tags ? book.tags.join(", ") : ""
  );
  const [saving, setSaving] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    // Convert reading_time and order to numbers if they're numeric fields
    if (name === "reading_time" || name === "order") {
      setFormState({
        ...formState,
        [name]: value ? parseInt(value) : null,
      });
    } else {
      setFormState({
        ...formState,
        [name]: value,
      });
    }
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagsInput(e.target.value);
    // Convert comma-separated string to array
    const tagsArray = e.target.value
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "");
    
    setFormState({
      ...formState,
      tags: tagsArray.length > 0 ? tagsArray : null,
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      // Validate required fields
      if (!formState.title || !formState.author || !formState.category) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        setSaving(false);
        return;
      }

      // Create the data object with required fields
      const bookData = {
        title: formState.title,
        author: formState.author,
        category: formState.category,
        description: formState.description,
        cover_image_url: formState.cover_image_url,
        tags: formState.tags,
        reading_time: formState.reading_time,
        templatelink: formState.templatelink,
        order: formState.order,
      };

      if (isEditing && book) {
        // Update existing book
        const { error } = await supabase
          .from("books")
          .update(bookData)
          .eq("id", book.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Book updated successfully",
        });
      } else {
        // Create new book
        const { error } = await supabase.from("books").insert(bookData);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Book created successfully",
        });
      }

      onClose(true); // Close form and refresh data
    } catch (error) {
      console.error("Error saving book:", error);
      toast({
        title: "Error",
        description: "Failed to save book",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Book" : "Add New Book"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="title" className="text-right">
              Title *
            </Label>
            <Input
              id="title"
              name="title"
              value={formState.title || ""}
              onChange={handleChange}
              className="col-span-3"
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="author" className="text-right">
              Author *
            </Label>
            <Input
              id="author"
              name="author"
              value={formState.author || ""}
              onChange={handleChange}
              className="col-span-3"
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formState.description || ""}
              onChange={handleChange}
              className="col-span-3"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="category" className="text-right">
              Category *
            </Label>
            <Input
              id="category"
              name="category"
              value={formState.category || ""}
              onChange={handleChange}
              className="col-span-3"
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="cover_image_url" className="text-right">
              Cover Image URL
            </Label>
            <Input
              id="cover_image_url"
              name="cover_image_url"
              value={formState.cover_image_url || ""}
              onChange={handleChange}
              className="col-span-3"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="tags" className="text-right">
              Tags
            </Label>
            <Input
              id="tags"
              name="tags"
              value={tagsInput}
              onChange={handleTagsChange}
              className="col-span-3"
              placeholder="tag1, tag2, tag3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="reading_time" className="text-right">
              Reading Time (min)
            </Label>
            <Input
              id="reading_time"
              name="reading_time"
              type="number"
              value={formState.reading_time || ""}
              onChange={handleChange}
              className="col-span-3"
              placeholder="30"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="templatelink" className="text-right">
              Template Link
            </Label>
            <Input
              id="templatelink"
              name="templatelink"
              value={formState.templatelink || ""}
              onChange={handleChange}
              className="col-span-3"
              placeholder="https://example.com/template"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="order" className="text-right">
              Display Order
            </Label>
            <Input
              id="order"
              name="order"
              type="number"
              value={formState.order || ""}
              onChange={handleChange}
              className="col-span-3"
              placeholder="1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
